import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Truck, Factory, Package, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { TrustBar } from "../components/shared/TrustBar";
import { SectionHeading } from "../components/shared/SectionHeading";
import { siteContent, images } from "../data/content";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
};

export const Home = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.hero})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/90 to-charcoal/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-6"
              data-testid="hero-title"
            >
              Coconut Shell Charcoal Briquette Manufacturer Based in Indonesia
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-lg md:text-xl text-zinc-300 mb-8 leading-relaxed"
            >
              Consistent, low-ash hookah charcoal. Export-ready with private label packaging.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Button
                asChild
                size="lg"
                className="bg-coconut text-charcoal hover:bg-coconut-hover font-heading uppercase tracking-wider text-base px-8"
                data-testid="hero-quote-btn"
              >
                <Link to="/contact">
                  Request a Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 font-heading uppercase tracking-wider text-base px-8"
                data-testid="hero-wa-btn"
              >
                <a
                  href={`${siteContent.contact.whatsappLink}?text=${encodeURIComponent(siteContent.contact.whatsappMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp Us
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trust Bar */}
      <TrustBar />

      {/* Product Spotlight */}
      <section className="py-24 bg-charcoal noise-overlay">
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-coconut text-sm tracking-wider uppercase mb-2 block">
                Our Product
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white mb-6">
                Coconut Shell Charcoal Briquette
              </h2>
              <ul className="space-y-4 mb-8">
                {siteContent.product.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-coconut/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-coconut" />
                    </div>
                    <span className="text-zinc-300">{benefit}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-4">
                <Button
                  asChild
                  className="bg-coconut text-charcoal hover:bg-coconut-hover font-heading uppercase"
                  data-testid="product-spotlight-btn"
                >
                  <Link to="/products">View Product Details</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 font-heading uppercase"
                >
                  <Link to="/contact">Get a Quote</Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-square bg-charcoal-subtle border border-white/5 overflow-hidden">
                <img
                  src={images.charcoalGlow}
                  alt="Premium coconut charcoal briquettes"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-coconut text-charcoal p-4 font-heading">
                <div className="text-3xl font-bold">25mm</div>
                <div className="text-sm uppercase">Cube Size</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Packaging & Private Label Preview */}
      <section className="py-24 bg-charcoal noise-overlay">
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1 grid grid-cols-2 gap-4"
            >
              <div className="aspect-square bg-charcoal-subtle border border-white/5 overflow-hidden">
                <img src={images.charcoalGlow} alt="Charcoal briquettes" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square bg-charcoal-subtle border border-white/5 overflow-hidden col-span-2">
                <img src={images.hookah} alt="Hookah charcoal" className="w-full h-full object-cover" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <span className="font-mono text-coconut text-sm tracking-wider uppercase mb-2 block">
                Private Label Services
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white mb-6">
                Your Brand, Our Quality
              </h2>
              <p className="text-zinc-300 mb-6 leading-relaxed">
                Use your own brand name and logo on our premium charcoal briquettes. 
                Custom box design, multiple languages, barcodes and labels. We accept custom orders.
              </p>
              <div className="grid grid-cols-1 gap-3 mb-8">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-coconut flex-shrink-0" />
                  <span className="text-zinc-300 text-sm">Use your own brand name and logo</span>
                </div>
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-coconut flex-shrink-0" />
                  <span className="text-zinc-300 text-sm">Custom order sizes accepted</span>
                </div>
                <div className="flex items-center gap-3">
                  <Factory className="w-5 h-5 text-coconut flex-shrink-0" />
                  <span className="text-zinc-300 text-sm">Custom box design with your artwork</span>
                </div>
                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-coconut flex-shrink-0" />
                  <span className="text-zinc-300 text-sm">FOB shipping from Indonesia</span>
                </div>
              </div>
              <Button
                asChild
                className="bg-coconut text-charcoal hover:bg-coconut-hover font-heading uppercase"
                data-testid="packaging-link-btn"
              >
                <Link to="/packaging">
                  Explore Private Label
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Logistics & Export */}
      <section className="py-24 bg-charcoal-paper">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            title="Export Ready"
            subtitle="Experienced in global shipping with comprehensive documentation support"
          />

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-charcoal border border-white/5 p-8"
            >
              <Truck className="w-10 h-10 text-coconut mb-4" />
              <h4 className="font-heading text-white uppercase text-lg mb-2">{siteContent.logistics.shipping.title}</h4>
              <p className="text-zinc-400 text-sm">
                {siteContent.logistics.shipping.description}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-charcoal border border-white/5 p-8"
            >
              <Factory className="w-10 h-10 text-coconut mb-4" />
              <h4 className="font-heading text-white uppercase text-lg mb-2">{siteContent.logistics.container.title}</h4>
              <p className="text-zinc-400 text-sm">
                {siteContent.logistics.container.description}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-charcoal border border-white/5 p-8"
            >
              <FileText className="w-10 h-10 text-coconut mb-4" />
              <h4 className="font-heading text-white uppercase text-lg mb-2">{siteContent.logistics.documentation.title}</h4>
              <p className="text-zinc-400 text-sm">
                {siteContent.logistics.documentation.description}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-charcoal noise-overlay">
        <div className="max-w-3xl mx-auto px-4 md:px-8 relative z-10">
          <SectionHeading
            title="Frequently Asked Questions"
            subtitle="Common questions from importers and distributors"
          />

          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {siteContent.faq.map((item, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-charcoal-paper border border-white/5 px-6"
              >
                <AccordionTrigger className="text-left font-heading uppercase text-white hover:text-coconut py-6">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 pb-6">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-coconut">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-charcoal mb-6">
              Ready to Partner with Us?
            </h2>
            <p className="text-charcoal/80 text-lg mb-8 max-w-2xl mx-auto">
              Get in touch for pricing, samples, or to discuss your private label requirements. 
              We typically respond within 24 hours.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-charcoal text-white hover:bg-charcoal-paper font-heading uppercase tracking-wider text-base px-8"
                data-testid="final-cta-quote"
              >
                <Link to="/contact">
                  Request a Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-charcoal text-charcoal hover:bg-charcoal/10 font-heading uppercase tracking-wider text-base px-8"
                data-testid="final-cta-wa"
              >
                <a
                  href={`${siteContent.contact.whatsappLink}?text=${encodeURIComponent(siteContent.contact.whatsappMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp Us
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
